/**
 * Agency Settings Page
 *
 * Modern, elegant settings page for agency configuration:
 * - General settings (name, email, timezone)
 * - Team management
 * - Whitelabel settings
 * - Billing & subscription
 *
 * Matches the modern Settings.tsx design pattern with:
 * - PageHeader component
 * - Pill-style tabs with URL persistence
 * - TimezoneSelect component
 * - Modern card styling with gradient accent bars
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { TeamManagement } from '@/components/agency/TeamManagement';
import { TimezoneSelect } from '@/components/common/TimezoneSelect';
import { agencyService } from '@/services/agency.service';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useAgencyGovernance } from '@/hooks/useAgencyGovernance';
import { BillingSettings } from '@/components/settings/BillingSettings';
import { useBrandSpaceUsage } from '@/hooks/useBrandSpaceUsage';
import { BrandSpaceUsageText } from '@/components/portfolio/BrandSpaceUsageText';

type AgencySettingsTab = 'general' | 'team' | 'whitelabel' | 'billing';

interface AgencySettings {
  agencyName: string;
  agencyEmail: string;
  timezone: string;
  packagePlan?: string;
  packageLimits?: {
    maxClients: number;
    maxPosts: number;
    maxTeamMembers: number;
  };
}

interface TeamMember {
  id: number;
  fullName: string;
  email: string;
  role: string;
}

export const AgencySettings = () => {
  const { t } = useTranslation(['agency', 'settings', 'common']);
  // -1 / null = no cap (same convention as team billing / Settings usage)
  const formatLimit = (value: number | null | undefined): string =>
    value === null || value === undefined || value < 0 ? t('settings_unlimited', { ns: 'settings' }) : value.toLocaleString();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // MANAGER can access team + whitelabel; billing stays OWNER/ADMIN only
  const isAgencyAdmin = user?.agencyRole === 'ADMIN' || user?.agencyRole === 'OWNER' || user?.agencyRole === 'MANAGER';
  // Writing name/email is OWNER/ADMIN only (managers see it read-only)
  const { canManageAgencyGovernance: canWriteSettings } = useAgencyGovernance();
  const canAccessBilling = user?.agencyRole === 'ADMIN' || user?.agencyRole === 'OWNER';
  const adminOnlyTabs = ['team', 'whitelabel', 'billing'];

  const [activeTab, setActiveTab] = useState<AgencySettingsTab>(() => {
    const tabParam = searchParams.get('tab') as AgencySettingsTab | null;
    if (tabParam && ['general', 'team', 'whitelabel', 'billing'].includes(tabParam)) {
      return tabParam;
    }
    return 'general';
  });
  const brandSpaceUsage = useBrandSpaceUsage(activeTab === 'billing');

  // Reset to general tab if non-admin tries to access admin-only tabs
  useEffect(() => {
    if (user && !isAgencyAdmin && adminOnlyTabs.includes(activeTab)) {
      setActiveTab('general');
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'general');
      setSearchParams(newParams, { replace: true });
    }
  }, [user, isAgencyAdmin, activeTab, searchParams, setSearchParams]);

  const [formData, setFormData] = useState({
    agencyName: '',
    agencyEmail: '',
    timezone: '',
  });

  // Fetch agency settings
  const { data: settings, isLoading: isLoadingSettings } = useQuery<AgencySettings>({
    queryKey: ['agency', 'settings'],
    queryFn: agencyService.getAgencySettings,
  });

  // Fetch team members
  const { data: teamMembers = [], isLoading: isLoadingTeam } = useQuery<TeamMember[]>({
    queryKey: ['agency', 'team'],
    queryFn: agencyService.getTeamMembers,
    enabled: activeTab === 'team',
  });

  // Initialize form data from fetched settings
  useEffect(() => {
    if (settings) {
      setFormData({
        agencyName: settings.agencyName || '',
        agencyEmail: settings.agencyEmail || '',
        timezone: settings.timezone || '',
      });
    }
  }, [settings]);

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: agencyService.updateAgencySettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'settings'] });
    },
  });

  const handleTabChange = useCallback((tab: AgencySettingsTab) => {
    if (tab === 'whitelabel') {
      navigate('/agency/settings/whitelabel');
      return;
    }
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  }, [navigate, searchParams, setSearchParams]);

  const handleUpdateSettings = (e: React.FormEvent) => {
    e.preventDefault();
    // Transform frontend field names to backend expected names
    // Note: email is read-only and not sent
    updateSettingsMutation.mutate({
      name: formData.agencyName,
      // timezone is not stored on agency yet - would need schema change
    });
  };

  const tabs = [
    { id: 'general' as const, label: t('agency_settings_tab_general'), icon: ICONS.SETTINGS },
    // Team + whitelabel: OWNER, ADMIN, MANAGER
    ...(isAgencyAdmin ? [
      { id: 'team' as const, label: t('agency_settings_tab_team'), icon: ICONS.USERS },
      { id: 'whitelabel' as const, label: t('agency_settings_tab_whitelabel'), icon: ICONS.PAINT },
    ] : []),
    // Billing: OWNER + ADMIN only
    ...(canAccessBilling ? [
      { id: 'billing' as const, label: t('agency_settings_tab_billing'), icon: ICONS.CREDIT_CARD },
    ] : []),
  ];

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Page Header */}
      <PageHeader
        title={t('agency_settings_page_title')}
        subtitle={t('agency_settings_page_subtitle')}
        icon={ICONS.SETTINGS}
        headerType="dashboard"
        documentTitle={t('agency_settings_page_title')}
      />

      {/* Mobile: Icon grid */}
      <div className="lg:hidden bg-gray-100/80 backdrop-blur-sm p-2 rounded-2xl">
        <div className="grid grid-cols-4 gap-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  'group flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200',
                  isActive ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  name={tab.icon}
                  size="sm"
                  color={isActive ? 'text-primary-600' : 'text-gray-500'}
                  ariaHidden
                />
                <span className={cn(
                  'text-[10px] font-medium leading-tight truncate max-w-full',
                  isActive ? 'text-gray-900' : 'text-gray-500'
                )}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: pill tabs */}
      <div className="hidden lg:flex bg-gray-100/80 backdrop-blur-sm p-1.5 rounded-2xl gap-1 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={cn(
              'group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 whitespace-nowrap',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            )}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            <Icon
              name={tab.icon}
              size="sm"
              color={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}
              ariaHidden
            />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-4 sm:space-y-6">
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
            <div className="p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Icon name={ICONS.SETTINGS} size="sm" color="text-primary-700" ariaHidden />
                {t('agency_settings_general_title')}
              </h2>

              {isLoadingSettings ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-10 bg-gray-200 rounded"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ) : (
                <form onSubmit={handleUpdateSettings} className="space-y-4 max-w-2xl">
                  {/* Non-admin notice */}
                  {!canWriteSettings && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                      <Icon name={ICONS.INFO} size="sm" color="text-amber-500" ariaHidden />
                      {t('agency_settings_view_only_notice')}
                    </div>
                  )}

                  <Input
                    label={t('agency_settings_general_name_label')}
                    value={formData.agencyName}
                    onChange={(e) => setFormData({ ...formData, agencyName: e.target.value })}
                    placeholder={t('agency_settings_general_name_placeholder')}
                    required
                    disabled={!canWriteSettings}
                  />

                  <div>
                    <Input
                      label={t('agency_settings_general_email_label')}
                      type="email"
                      value={formData.agencyEmail}
                      placeholder={t('agency_settings_general_email_placeholder')}
                      disabled // Email is always read-only
                    />
                    <p className="mt-1 text-xs text-gray-500">{t('agency_settings_email_readonly_hint')}</p>
                  </div>

                  <TimezoneSelect
                    value={formData.timezone}
                    onChange={(tz) => setFormData({ ...formData, timezone: tz })}
                    label={t('agency_settings_general_timezone_label')}
                    showCurrentTime
                    disabled={!canWriteSettings}
                  />

                  {/* Only show save button for admins */}
                  {canWriteSettings && (
                    <div className="flex items-center gap-3 pt-2">
                      <Button type="submit" loading={updateSettingsMutation.isPending}>
                        {t('agency_settings_general_save_button')}
                      </Button>

                      {updateSettingsMutation.isSuccess && (
                        <div className="flex items-center gap-2 text-sm text-green-700">
                          <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
                          {t('agency_settings_general_success')}
                        </div>
                      )}

                      {updateSettingsMutation.isError && (
                        <div className="flex items-center gap-2 text-sm text-red-700">
                          <Icon name={ICONS.CAUTION} size="sm" color="text-red-600" ariaHidden />
                          {t('agency_settings_general_error')}
                        </div>
                      )}
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-coral" />
            <div className="p-4 sm:p-6">
              {isLoadingTeam ? (
                <div className="animate-pulse space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-20 bg-gray-200 rounded"></div>
                  ))}
                </div>
              ) : (
                <TeamManagement teamMembers={teamMembers as import("@/types/agency").TeamMember[]} />
              )}
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Current Plan */}
            {settings?.packagePlan && (
              <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-mint shadow-lg">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
                <div className="p-4 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div>
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 flex items-center gap-2">
                        <Icon name={ICONS.CROWN} size="sm" color="text-primary-600" ariaHidden className="sm:!text-lg" />
                        {t('agency_settings_billing_plan_title', { plan: settings.packagePlan })}
                      </h3>
                      <p className="text-xs sm:text-base text-gray-600">{t('agency_settings_billing_plan_subtitle')}</p>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-1.5"
                      onClick={() => navigate('/shop')}
                    >
                      <Icon name={ICONS.ARROW_UP} size="sm" color="text-white" ariaHidden />
                      {t('agency_settings_billing_upgrade_button')}
                    </Button>
                  </div>

                  {/* Package Limits */}
                  {settings.packageLimits && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      {/* Clients */}
                      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
                        <div className="p-3 sm:p-4">
                          <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-mint flex items-center justify-center flex-shrink-0">
                              <Icon name={ICONS.USERS} size="sm" color="text-primary-700" ariaHidden />
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-gray-700">{t('agency_settings_billing_clients')}</div>
                          </div>
                          <div className="text-xl sm:text-3xl font-bold text-gray-900">
                            {formatLimit(settings.packageLimits.maxClients)}
                          </div>
                          <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">{t('agency_settings_billing_clients_max')}</div>
                          <BrandSpaceUsageText
                            usage={brandSpaceUsage}
                            className="block text-[11px] sm:text-xs text-gray-600 mt-1.5 sm:mt-2"
                          />
                        </div>
                      </div>

                      {/* Posts per Month */}
                      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-coral" />
                        <div className="p-3 sm:p-4">
                          <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-coral flex items-center justify-center flex-shrink-0">
                              <Icon name={ICONS.PAPER_PLANE} size="sm" color="text-green-600" ariaHidden />
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-gray-700">{t('agency_settings_billing_posts')}</div>
                          </div>
                          <div className="text-xl sm:text-3xl font-bold text-gray-900">
                            {formatLimit(settings.packageLimits.maxPosts)}
                          </div>
                          <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">{t('agency_settings_billing_posts_per')}</div>
                        </div>
                      </div>

                      {/* Team Members */}
                      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-peach" />
                        <div className="p-3 sm:p-4">
                          <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-peach flex items-center justify-center flex-shrink-0">
                              <Icon name={ICONS.USERS} size="sm" color="text-secondary-600" ariaHidden />
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-gray-700">{t('agency_settings_billing_members')}</div>
                          </div>
                          <div className="text-xl sm:text-3xl font-bold text-gray-900">
                            {formatLimit(settings.packageLimits.maxTeamMembers)}
                          </div>
                          <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">{t('agency_settings_billing_members_max')}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Subscription summary + invoice list (live, from WooCommerce) */}
            <BillingSettings scope="agency" />
          </div>
        )}
      </div>
    </div>
  );
};
